<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useQuasar } from 'quasar';

import ReorderableTable from '@/components/ReorderableTable.vue';
import TimelineStepDialog from './dialogs/TimelineStepDialog.vue';

import { useTimelineStepsStore } from '@/stores/timelineSteps.store';
import type { TimelineStepDto, ProjectModuleDto, Locales } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';

// CONST
const { t } = useI18n();
const $q = useQuasar();

// STORE
const timelineStepsStore = useTimelineStepsStore();
const { orderedTimelineSteps } = storeToRefs(timelineStepsStore);

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
}>();

const statuses = timelineStepsStore.getTimelineStepStatus();

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.timeline-step'),
    field: (row: TimelineStepDto) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: TimelineStepDto) => statuses.find((x) => x.value === row.status)?.label,
    align: 'left',
  },
]);

// LIFECYCLE
onMounted(async () => {
  if (props.module.id) {
    await timelineStepsStore.fetchTimelineStepsByModuleId(props.module.id);
  }
});

// FUNCTIONS
const handleAddRow = () => {
  if (props.module.id) {
    const timelineStep: TimelineStepDto = timelineStepsStore.createTimelineStep(props.module.id);
    $q.dialog({
      component: TimelineStepDialog,
      componentProps: {
        mode: 'add',
        timelineStep,
        languageEnabled: props.languageEnabled,
      },
    });
  }
};

const handleDeleteRow = (row: TimelineStepDto) => {
  $q.dialog({
    title: t('global.dialogs.deleteTitle', { type: t('project-module.name') }),
    message: t('global.dialogs.deleteMessage', { type: t('project-module.name') }),
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await timelineStepsStore.deleteTimelineStep(row);
  });
};

const handleRowClick = (row: TimelineStepDto) => {
  $q.dialog({
    component: TimelineStepDialog,
    componentProps: {
      mode: 'edit',
      timelineStep: row,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleReorder = (source: TimelineStepDto, target: TimelineStepDto) => {
  timelineStepsStore.updateTimelineSteps(source, target);
};
</script>
<template>
  <div class="timeline-step-list">
    <reorderable-table
      :rows="orderedTimelineSteps"
      :columns="columns"
      :title="t('project-module.timeline.title')"
      itemKey="id"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @reorder="handleReorder"
    />
  </div>
</template>
